/**
 * MSAL.js Authentication Configuration
 * 
 * This configuration supports both:
 * 1. Browser-based OAuth login (interactive)
 * 2. Teams Tab SSO (silent token acquisition)
 */

import { Configuration, LogLevel } from '@azure/msal-browser';

// Get environment variables injected by backend into window.env
declare global {
  interface Window {
    env?: {
      AZURE_CLIENT_ID?: string;
      AZURE_TENANT_ID?: string;
      AZURE_REDIRECT_URI?: string;
      AZURE_APP_ID_URI?: string;
    };
  }
}

// Environment variables for Azure AD (injected from backend at runtime)
const AZURE_CLIENT_ID = window.env?.AZURE_CLIENT_ID || '';
const AZURE_TENANT_ID = window.env?.AZURE_TENANT_ID || 'common';
const AZURE_REDIRECT_URI = window.env?.AZURE_REDIRECT_URI || window.location.origin + '/auth/oauth/azure-ad/callback';
const AZURE_APP_ID_URI = window.env?.AZURE_APP_ID_URI || (AZURE_CLIENT_ID ? `api://${AZURE_CLIENT_ID}` : '');

// Azure AD authority
const authority = `https://login.microsoftonline.com/${AZURE_TENANT_ID}`;

/**
 * MSAL Configuration
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: AZURE_CLIENT_ID,
    authority: authority,
    redirectUri: AZURE_REDIRECT_URI,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage', // Use localStorage for persistence across tabs
    storeAuthStateInCookie: false, // Set to true if you have issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

/**
 * Scopes for token acquisition
 */
const apiScopes = AZURE_APP_ID_URI
  ? [`${AZURE_APP_ID_URI}/user_impersonation`]
  : ['openid', 'profile', 'email'];

export const loginRequest = {
  scopes: apiScopes,
};

/**
 * Scopes for API access
 */
export const tokenRequest = {
  scopes: apiScopes,
  forceRefresh: false,
};

/**
 * Check if running inside Microsoft Teams
 */
export const isInTeams = (): boolean => {
  // Check URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('inTeams') === 'true') {
    return true;
  }

  // Check if Teams SDK is available and initialized
  try {
    return !!(window as any).microsoftTeams;
  } catch {
    return false;
  }
};

/**
 * Get the current environment
 */
export const getEnvironment = () => {
  return {
    clientId: AZURE_CLIENT_ID,
    tenantId: AZURE_TENANT_ID,
    redirectUri: AZURE_REDIRECT_URI,
    authority: authority,
    isTeams: isInTeams(),
  };
};
